import React, { useState, useEffect } from "react";
import styles from "./StatFacturation.module.sass";
import cn from "classnames";
import Card from "../../../components/Card";
import Icon from "../../../components/Icon";
import Chart from "./Chart";
import { getInvoiceStatsOpco } from "../../../services/invoice.service";
import { useAuth } from "../../../contexts/AuthContext";

// Générer les années disponibles pour la navigation
const generateAvailableYears = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  
  // Générer 5 années (2 précédentes, actuelle, 2 suivantes)
  for (let i = -2; i <= 2; i++) {
    years.push({
      year: currentYear + i,
      display: `${currentYear + i}`
    });
  }
  
  return years;
};

const StatFacturation = ({ className }) => {
  const { currentUser } = useAuth();
  const availableYears = generateAvailableYears();
  const [currentYearIndex, setCurrentYearIndex] = useState(2); // Année actuelle (milieu du tableau)
  const [chartData, setChartData] = useState([]); // Données vides par défaut
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const currentYear = availableYears[currentYearIndex];

  // Fonction pour charger les données depuis l'API (données annuelles)
  const loadInvoiceStats = async (year) => {
    try {
      setLoading(true);
      setError(null);
      console.log(`📊 Chargement des statistiques pour l'année ${year}`);
      
      if (!currentUser?.partner_id) {
        throw new Error('Partner ID non trouvé. Veuillez vous reconnecter.');
      }
      
      console.log(`🔧 Utilisation du partenaire: ${currentUser.partner_id}`);
      
      const response = await getInvoiceStatsOpco(parseInt(year), currentUser.partner_id);
      console.log('📋 Réponse complète de l\'API:', response);
      
      if (response.success && response.chartData) {
        setChartData(response.chartData);
      } else {
        console.log('❌ Pas de données valides, utilisation des données vides');
        setChartData([]);
      }
    } catch (err) {
      console.error('❌ Erreur lors du chargement des statistiques:', err);
      setError(`Erreur: ${err.message}`);
      setChartData([]);
    } finally {
      setLoading(false);
    }
  };

  // Charger les données au montage initial
  useEffect(() => {
    loadInvoiceStats(currentYear.year);
  }, []);

  const goToPreviousYear = () => {
    if (currentYearIndex > 0) {
      const newIndex = currentYearIndex - 1;
      setCurrentYearIndex(newIndex);
      const newYear = availableYears[newIndex];
      loadInvoiceStats(newYear.year);
    }
  };

  const goToNextYear = () => {
    if (currentYearIndex < availableYears.length - 1) {
      const newIndex = currentYearIndex + 1;
      setCurrentYearIndex(newIndex);
      const newYear = availableYears[newIndex];
      loadInvoiceStats(newYear.year);
    }
  };

  return (
    <Card
      className={cn(styles.card, className)}
      title="Facturation"
      classTitle={cn("title-purple", styles.cardTitle)}
      classCardHead={styles.cardHead}
      head={
        <div className={styles.navigationContainer}>
          <button 
            className={cn(styles.navButton, { [styles.disabled]: currentYearIndex === 0 || loading })}
            onClick={goToPreviousYear}
            disabled={currentYearIndex === 0 || loading}
          >
            <Icon name="arrow-left" size="16" />
          </button>
          <span className={styles.currentPeriod}>
            {loading ? 'Chargement...' : (currentYear?.display || '2024')}
          </span>
          <button 
            className={cn(styles.navButton, { [styles.disabled]: currentYearIndex === availableYears.length - 1 || loading })}
            onClick={goToNextYear}
            disabled={currentYearIndex === availableYears.length - 1 || loading}
          >
            <Icon name="arrow-right" size="16" />
          </button>
        </div>
      }
    >
      <div className={styles.overview}>
        <Chart data={chartData} loading={loading} />
      </div>
    </Card>
  );
};

export default StatFacturation;
